import React, { useState, useEffect, useLayoutEffect } from "react";
import { useFormik } from "formik";
import Modal from "components/UI/Modal/Modal";
import { InsertModal, EstadoModal, DetalleModal } from "./Actions";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { HeaderButton } from "components";
import Filters from "components/Filters";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import PublishedWithChangesRounded from '@mui/icons-material/PublishedWithChangesRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import { Buttons } from "components";
import { ACTIONS } from "consts/Actions";
import { RNHEstablecimientoGet } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoGet";
import { RNHEstablecimientoEstructuraGet } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoEstructuraGet";
import {
  agentesTitles,
  agentesColumnGroups,
} from "consts/titulos-de-tablas";
import CeldaTruncada from "components/UI/Grid/CeldaTruncada";
import styles from "styles/pages/Pages.module.scss";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import XGrid from "components/UI/Grid/XGrid";
import { RNHAgenteGet } from "services/services-mia/programa-salud/personal/RNHAgenteGet";
import { estaDeBaja, etiquetaEstado } from "./consts";

const PUEDE_EDITAR_ESTADO_CONEXION = true;

const FILTROS_VACIOS = {
  establecimientoCod: "",
  estructuraCod: "",
  apellidos: "",
  nroDocumento: "",
};

/**
 * Las columnas que CORTAN con puntos suspensivos y muestran el valor entero en un
 * tooltip al pasar el mouse. Son las dos de texto largo de la fila: el establecimiento
 * —"Hospital Dr. Ramon Carrillo"— y el cargo, que tampoco entra en sus 150px.
 *
 * El tooltip solo sale cuando el texto NO entra; de eso se encarga `CeldaTruncada`.
 */
const CAMPOS_TRUNCADOS = ["lugar", "cargo"];

/**
 * El renderCell se inyecta aca y no en `titulos-de-tablas` para que ese archivo siga
 * siendo solo datos, sin importar componentes. Es una constante de modulo: recalcularlo
 * en cada render le cambiaria la identidad a las columnas al grid.
 */
const titles = agentesTitles.map((t) =>
  CAMPOS_TRUNCADOS.includes(t.field)
    ? { ...t, renderCell: ({ value }) => <CeldaTruncada value={value} /> }
    : t
);

/**
 * Listado de agentes del programa.
 *
 * LA FILA DEL LISTADO TIENE MENOS CAMPOS QUE EL DETALLE. No trae la version, el
 * titulo, el mail ni los cods de cargo y convenio: cada modal los pide aparte con
 * `RNHAgenteGet` por id. Mapearlos igual daba `undefined` y escondia el problema hasta
 * que un Upd fallaba por version vacia.
 *
 * LOS DOS FILTROS DE REPARTICION VIAJAN JUNTOS. `rnhTipoComponenteCod` y
 * `rnhAgenteEstablecimientoEstructuraCod` o van los dos o el back responde 42003. Por
 * eso el filtro guarda el cod y el tipo se resuelve contra el catalogo al enviar.
 *
 * REFRESCAR RESPETA LOS FILTROS. Si volviera a traer todo, el usuario perderia el
 * recorte despues de cada alta o cambio de estado.
 *
 * LO QUE PASA DENTRO DEL DETALLE SE REFRESCA AL CERRAR, no en el momento: `getAgentes`
 * limpia la fila elegida, y el detalle se alimenta justo de esa fila.
 *
 * Dos cuidados del mapeo: `??` donde un 0 real es un dato (la cantidad de mesas) y
 * `|| null` en las fechas, porque `transformDate` corta por posicion y con "" da "//".
 */
export default function Agentes() {
  const credentials = useRecoilValue(userCredentials);
  const setRoute = useSetRecoilState(routeInfo);
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [loading, setLoading] = useState(false);
  const [agentes, setAgentes] = useState([]);
  const [hospitales, setHospitales] = useState([]);
  const [reparticiones, setReparticiones] = useState([]);
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
  const agenteDeBaja = estaDeBaja(rowSelected.row.statusCod);
  const { service } = useService();

  const getAgentes = async (filtros = FILTROS_VACIOS) => {
    setAgentes([]);
    setRowSelected({ status: false, row: {} });
    setLoading(true);

    const reparticion = reparticiones.find(
      (r) => String(r.cod) === String(filtros.estructuraCod)
    );

    const respuesta = await RNHAgenteGet(credentials, {
      establecimientoCod: filtros.establecimientoCod,
      estructuraCod: reparticion?.cod,
      tipoComponenteCod: reparticion?.tipo,
      apellidos: filtros.apellidos,
      nroDocumento: filtros.nroDocumento,
    });
    setLoading(false);
    if (!respuesta.ok) return;

    const newRows = respuesta.data.map((data) => {
      return {
        id: data.rnhAgenteId,
        apellidosNombres: [data.usuarioApellidos, data.usuarioNombres]
          .filter(Boolean)
          .join(", "),
        documento: data.usuarioNroDeDocumentoX,
        legajo: data.rnhAgenteLegajo ?? "",
        convenio: data.rnhConvenioLabel,
        lugar: data.rnhLugarAsignadoNombre ?? "Sin asignar",
        unique: data.rnhAgenteEstablecimientoUnico ?? "",
        mesasCantidad: data.rnhAgenteMesasCantidad ?? null,
        dob: data.usuarioDOB || null,
        fechaAlta: data.rnhAgenteFechaInicio || null,
        cargo: data.rnhCargoNombre || "Sin cargo",
        status: etiquetaEstado(data.usuarioVinculoStatus),
        statusCod: data.usuarioVinculoStatus,
        fechaCarga: data.usuarioVinculoUFH,
      };
    });

    setAgentes(newRows);
  }
  const [huboCambios, setHuboCambios] = useState(false);

  const formik = useFormik({
    initialValues: FILTROS_VACIOS,
    onSubmit: (values) => getAgentes(values),
  });

  const refrescar = () => getAgentes(formik.values);

  const borrarFiltros = () => {
    formik.setValues(FILTROS_VACIOS);
    getAgentes(FILTROS_VACIOS);
  };

  const hayFiltros = Object.values(formik.values).some(Boolean);

  const hospitalElegido = formik.values.establecimientoCod;

  useEffect(() => {
    formik.setFieldValue("estructuraCod", "");
    getReparticiones(hospitalElegido);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalElegido, hospitales]);

  const closeModal = () => {
    setModalDetails({ isOpen: false });
    if (huboCambios) {
      setHuboCambios(false);
      refrescar();
    }
  };
  const handleClick = ({ procede, title }) => {
    setModalDetails({ procede, title, isOpen: true });
  };

  const getHospitales = async () => {
    const { ok, data } = await RNHEstablecimientoGet(credentials, { tipo: "H" });
    setHospitales(ok ? (data ?? []).filter((h) => h?.rnhEstablecimientoCod) : []);
  };

  const getReparticiones = async (establecimientoCod) => {
    if (!establecimientoCod) {
      setReparticiones([]);
      return;
    }
    const hospital = hospitales.find(
      (h) => String(h.rnhEstablecimientoCod) === String(establecimientoCod)
    );
    if (!hospital) {
      setReparticiones([]);
      return;
    }

    const { ok, data } = await RNHEstablecimientoEstructuraGet(credentials, {
      rnhEstablecimientoEstructuraHospitalId: hospital.rnhEstablecimientoId,
      listAll: "Y",
    });
    setReparticiones(
      ok
        ? (data ?? [])
            .filter((d) => d?.rnhEstructuraCod)
            .map((d) => ({
              cod: d.rnhEstructuraCod,
              nombre: d.rnhEstructuraNombre ?? d.rnhEstructuraCod,
              tipo: d.rnhTipoEstructuraCod,
            }))
        : []
    );
  };

  useEffect(() => {
    getAgentes();
    getHospitales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useLayoutEffect(() => {
    setRoute({
      wordKey: `Agentes - Programa Salud RN`
    })
  }, [service.servicioNombre, setRoute])

  return (
    <main className={styles.main}>
      <Modal
        size={
          modalDetails.procede === ACTIONS.VIEW
            ? "xl"
            : modalDetails.procede === ACTIONS.STATUS
              ? "sm"
              : undefined
        }
        fit
        open={modalDetails.isOpen}
        title={modalDetails.title}
        onClose={closeModal}
      >
        {modalDetails.procede === ACTIONS.VIEW && (
          <DetalleModal
            row={rowSelected.row}
            onCambio={() => setHuboCambios(true)}
          />
        )}
        {modalDetails.procede === ACTIONS.STATUS && (
          <EstadoModal
            row={rowSelected.row}
            updateList={refrescar}
            closeModal={closeModal}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            servicio={service}
            updateList={refrescar}
            closeModal={closeModal}
          />
        )}
      </Modal>
      <div className={styles["filters-actions"]}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Filters
            formik={formik}
            inputsRender={[
              {
                type: "autocomplete",
                name: "establecimientoCod",
                label: "Establecimiento",
                placeholder: "Todos",
                disabled: loading,
                options: hospitales,
                optionValue: "rnhEstablecimientoCod",
                optionLabel: "rnhEstablecimientoNombre",
                width: "260px",
              },
              {
                type: "autocomplete",
                name: "estructuraCod",
                label: "Reparticion",
                placeholder: hospitalElegido ? "Todas" : "Elegí un hospital",
                disabled: loading || !hospitalElegido,
                options: reparticiones,
                optionValue: "cod",
                optionLabel: "nombre",
                width: "220px",
              },
              {
                type: "text",
                name: "apellidos",
                label: "Apellido",
                placeholder: "Buscar",
                disabled: loading,
              },
              {
                type: "text",
                name: "nroDocumento",
                label: "Documento",
                placeholder: "Buscar",
                disabled: loading,
              },
            ]}
            disabled={loading}
          />
          <HeaderButton
            text={"Borrar filtros"}
            onClick={borrarFiltros}
            disabled={loading || !hayFiltros}
          >
            <FilterAltOffRoundedIcon />
          </HeaderButton>
        </div>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={refrescar}
            disabled={rowSelected.row.id || false}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Nuevo Agente"}
            handleClick={handleClick}
            data={{
              title: "Nuevo Agente",
              procede: ACTIONS.INSERT
            }}
            disabled={!puedeEditarServicio(service) || rowSelected.row.id}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Detalle del Agente"}
            handleClick={handleClick}
            data={{
              title: "Detalle del Agente",
              procede: ACTIONS.VIEW
            }}
            disabled={!rowSelected.row.id}
          >
            <VisibilityRounded />
          </HeaderButton>
          <HeaderButton
            text={
              PUEDE_EDITAR_ESTADO_CONEXION
                ? "Modificar Estado"
                : "Modificar Estado — el servicio todavía no lo permite"
            }
            handleClick={handleClick}
            data={{
              title: "Modificar Estado",
              procede: ACTIONS.STATUS
            }}
            disabled={
              !PUEDE_EDITAR_ESTADO_CONEXION ||
              !puedeEditarServicio(service) ||
              !rowSelected.row.id ||
              agenteDeBaja
            }
          >
            <PublishedWithChangesRounded />
          </HeaderButton>
        </Buttons>
      </div>
      <div className={styles.container}>
        <XGrid
          rows={agentes}
          titles={titles}
          columnGroups={agentesColumnGroups}
          loading={loading}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>
    </main>
  );
}